// One board: the Excalidraw canvas, wired to the collaboration socket.

import { Excalidraw, exportToBlob, MainMenu, WelcomeScreen } from "@excalidraw/excalidraw";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api.js";
import { useCollab } from "../collab/useCollab.js";
import { Comments } from "../comments/Comments.jsx";
import { Avatar } from "../ui/Avatar.jsx";
import { FormModal } from "../ui/Modal.jsx";
import { TagField, TextField } from "../ui/Field.jsx";
import { Back, Comment } from "../ui/icons.jsx";
import { Skeleton } from "../ui/Skeleton.jsx";
import { actorName, exactTime, relativeTime } from "../ui/format.js";
import { useTheme } from "../ui/theme.js";

const THUMBNAIL_INTERVAL_MS = 60_000;
const THUMBNAIL_MAX_PX = 480;

const CONNECTION = {
  connecting: { label: "connecting", tone: "warn" },
  live: { label: "live", tone: "ok" },
  reconnecting: { label: "reconnecting", tone: "warn" },
  offline: { label: "offline", tone: "bad" },
};

/**
 * Whether the work is safe, said plainly. This is the one piece of status worth
 * a permanent place on screen: everything else here is recoverable, and this is
 * not.
 */
const SaveState = ({ save, connection }) => {
  if (save.error) {
    return (
      <span
        className="pill bad"
        title={`The server could not save: ${save.error}. Retrying. Your work is also kept in this browser.`}
      >
        <span className="dot" />
        not saved
      </span>
    );
  }
  if (connection === "offline" || save.state === "unsent") {
    return (
      <span
        className="pill bad"
        title="No connection. Your edits are saved in this browser and upload when it returns."
      >
        <span className="dot" />
        offline
      </span>
    );
  }
  if (save.state === "saving") {
    return (
      <span className="pill warn" title="Saving your changes.">
        <span className="dot" />
        saving
      </span>
    );
  }
  return (
    <span className="pill ok" title={save.at ? `Last saved ${exactTime(save.at)}` : "All changes saved."}>
      <span className="dot" />
      {save.at ? `saved ${relativeTime(save.at)}` : "saved"}
    </span>
  );
};

export const BoardPage = () => {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const { resolved: theme } = useTheme();
  const [excalidrawAPI, setExcalidrawAPI] = useState(null);
  const [board, setBoard] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [commentMode, setCommentMode] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [activeThread, setActiveThread] = useState(null);
  const dirtyRef = useRef(false);

  const { status, ready, collaborators, me, save, comments, addComment, setResolved, onChange, onPointerUpdate } =
    useCollab({ boardId, excalidrawAPI });
  const openThreads = comments.filter((c) => !c.parentId && !c.resolved).length;

  const loadBoard = useCallback(
    async ({ signal } = {}) => {
      try {
        const res = await api.getBoard(boardId);
        if (!signal?.aborted) setBoard(res.board);
      } catch (err) {
        if (!signal?.aborted) setError(err.message);
      }
    },
    [boardId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void loadBoard({ signal: controller.signal });
    return () => controller.abort();
  }, [loadBoard]);

  // Board card previews. Rendering one costs a canvas export, so it happens on a
  // slow timer and only when something actually changed since the last one.
  const captureThumbnail = useCallback(async () => {
    if (!excalidrawAPI || !dirtyRef.current) return;
    const elements = excalidrawAPI.getSceneElements();
    if (elements.length === 0) return;
    dirtyRef.current = false;
    try {
      const blob = await exportToBlob({
        elements,
        appState: { exportBackground: true, viewBackgroundColor: "#ffffff" },
        files: excalidrawAPI.getFiles(),
        maxWidthOrHeight: THUMBNAIL_MAX_PX,
        mimeType: "image/png",
        exportPadding: 16,
      });
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      await api.putThumbnail(boardId, dataUrl);
    } catch (err) {
      dirtyRef.current = true; // try again on the next tick
      console.warn("[canvas] could not save a board preview", err);
    }
  }, [boardId, excalidrawAPI]);

  useEffect(() => {
    const timer = setInterval(() => void captureThumbnail(), THUMBNAIL_INTERVAL_MS);
    return () => {
      clearInterval(timer);
      void captureThumbnail();
    };
  }, [captureThumbnail]);

  const handleChange = useCallback(
    (elements, appState, files) => {
      dirtyRef.current = true;
      onChange(elements, appState, files);
    },
    [onChange],
  );

  if (error) {
    return (
      <div className="centre-screen">
        <div className="card narrow">
          <h1>Could not open this board</h1>
          <p className="muted">{error}</p>
          <Link className="btn" to="/" style={{ marginTop: 18 }}>
            Back to boards
          </Link>
        </div>
      </div>
    );
  }

  const connection = CONNECTION[status] ?? { label: status, tone: "warn" };
  const madeBy = board ? actorName(board.createdByName, board.createdByEmail, board.createdBy) : null;

  return (
    <div className="board-screen">
      <header className="board-bar">
        <div className="board-bar-left">
          <Link
            to={board ? `/f/${board.folderId}` : "/"}
            className="btn ghost icon small"
            title="Back to the folder"
          >
            <Back />
          </Link>

          {board ? (
            <>
              <button
                className="board-title"
                onClick={() => setEditing(true)}
                title="Rename or edit tags"
              >
                {board.name}
              </button>
              {board.tags?.length ? (
                <div className="tags">
                  {board.tags.map((tag) => (
                    <span className="tag" key={tag}>
                      <span className="sq" />
                      {tag}
                    </span>
                  ))}
                </div>
              ) : null}
              <span className="muted small nowrap" title={exactTime(board.createdAt)}>
                by {madeBy}
              </span>
            </>
          ) : (
            <Skeleton width={180} height={16} radius={6} style={{ marginLeft: 6 }} />
          )}
        </div>

        <div className="board-bar-right">
          <button
            type="button"
            className={`btn small${commentMode ? " active" : ""}`}
            aria-pressed={commentMode}
            title="Leave a comment on the board (C)"
            onClick={() => setCommentMode((on) => !on)}
          >
            <Comment />
            Comment
          </button>
          <button
            type="button"
            className={`btn small${commentsOpen ? " active" : ""}`}
            aria-pressed={commentsOpen}
            title="Show all comment threads"
            onClick={() => setCommentsOpen((open) => !open)}
          >
            Comments
            {openThreads ? <span className="count">{openThreads}</span> : null}
          </button>
          {/* Nothing is claimed about the connection or the saving until the
              board has actually loaded. The bar used to say "saved" and "live"
              over an empty canvas that had not arrived yet. */}
          {ready ? (
            <>
              <SaveState save={save} connection={status} />
              <span className={`pill ${connection.tone}`} title={`Connection: ${connection.label}`}>
                <span className="dot" />
                {connection.label}
              </span>
            </>
          ) : (
            <span className="pill">
              <span className="dot" />
              opening
            </span>
          )}
          <div className="collab-avatars">
            {collaborators.map((person) => (
              <Avatar key={person.id} user={{ name: person.name }} color={person.color} size={26} />
            ))}
            {me ? (
              <Avatar user={{ name: me.name }} color={me.color} size={26} title={`${me.name} (you)`} />
            ) : null}
          </div>
        </div>
      </header>

      <div className={`canvas-holder${commentMode ? " commenting" : ""}`}>
        {ready ? null : <div className="canvas-veil" aria-hidden="true" />}
        <Excalidraw
          excalidrawAPI={setExcalidrawAPI}
          onChange={handleChange}
          onPointerUpdate={onPointerUpdate}
          isCollaborating
          theme={theme}
          initialData={{ appState: { viewBackgroundColor: "#ffffff" } }}
          UIOptions={{ canvasActions: { loadScene: false } }}
        >
          <MainMenu>
            <MainMenu.DefaultItems.ToggleTheme />
            <MainMenu.DefaultItems.ChangeCanvasBackground />
            <MainMenu.DefaultItems.SaveAsImage />
            <MainMenu.Item onSelect={() => navigate(board ? `/f/${board.folderId}` : "/")}>
              Back to the folder
            </MainMenu.Item>
          </MainMenu>
          {/* Only once the scene is in. Otherwise it advertises an empty board
              for the moment before the drawing arrives. */}
          {ready && board ? (
            <WelcomeScreen>
              <WelcomeScreen.Center>
                <WelcomeScreen.Center.Heading>{board.name}</WelcomeScreen.Center.Heading>
                <WelcomeScreen.Center.Menu>
                  <WelcomeScreen.Center.MenuItemHelp />
                </WelcomeScreen.Center.Menu>
              </WelcomeScreen.Center>
            </WelcomeScreen>
          ) : null}
        </Excalidraw>
        {ready ? (
          <Comments
            excalidrawAPI={excalidrawAPI}
            comments={comments}
            me={me}
            mode={commentMode}
            onMode={setCommentMode}
            panelOpen={commentsOpen}
            onPanel={setCommentsOpen}
            activeId={activeThread}
            onActive={setActiveThread}
            onAdd={addComment}
            onResolve={setResolved}
          />
        ) : null}
      </div>

      {editing && board ? (
        <EditBoard board={board} onClose={() => setEditing(false)} onSaved={setBoard} />
      ) : null}
    </div>
  );
};

const EditBoard = ({ board, onClose, onSaved }) => {
  const [name, setName] = useState(board.name);
  const [tags, setTags] = useState(board.tags ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const { board: updated } = await api.updateBoard(board.id, { name: name.trim(), tags });
      onSaved(updated);
      onClose();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <FormModal
      title="Edit this board"
      submitLabel="Save"
      canSubmit={name.trim().length > 0}
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={submit}
    >
      <TextField
        label="Name"
        value={name}
        maxLength={120}
        onChange={(event) => setName(event.target.value)}
      />
      <TagField value={tags} onChange={setTags} />
    </FormModal>
  );
};
